import React, { useCallback, useEffect, useRef, useState } from 'react';

/** Minimal shape DropdownMenu needs from the trigger element — it reads
 * `onClick` and clones in `onClick`/`aria-haspopup`/`aria-expanded`. React
 * 19's `ReactElement` defaults its props type param to `unknown` (not
 * `any`), so this has to be explicit rather than left to inference. */
type DropdownTriggerProps = {
  onClick?: (e: React.MouseEvent) => void;
  'aria-haspopup'?: React.AriaAttributes['aria-haspopup'];
  'aria-expanded'?: boolean;
};

export interface DropdownMenuProps {
  /** The button (or any element) that opens the menu. Must be a single element. */
  trigger: React.ReactElement<DropdownTriggerProps>;
  /** Menu items. Use <DropdownMenu.Item> for proper styling and close-on-click behavior. */
  children: React.ReactNode;
  /** Alignment of the menu panel relative to the trigger. Defaults to 'right'. */
  align?: 'left' | 'right';
  /** Extra classes on the menu panel. */
  menuClassName?: string;
}

interface DropdownMenuContextValue {
  close: () => void;
}

const DropdownMenuContext = React.createContext<DropdownMenuContextValue | null>(null);

export const DropdownMenu: React.FC<DropdownMenuProps> & {
  Item: React.FC<DropdownItemProps>;
  Separator: React.FC;
} = ({ trigger, children, align = 'right', menuClassName = '' }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const clonedTrigger = React.cloneElement(trigger, {
    onClick: (e: React.MouseEvent) => {
      trigger.props.onClick?.(e);
      if (!e.defaultPrevented) {
        setOpen((v) => !v);
      }
    },
    'aria-haspopup': 'menu',
    'aria-expanded': open,
  });

  const alignClass = align === 'right' ? 'right-0' : 'left-0';

  return (
    <div ref={containerRef} className="relative inline-block">
      {clonedTrigger}
      {open && (
        <DropdownMenuContext.Provider value={{ close }}>
          <div
            role="menu"
            className={`absolute ${alignClass} mt-2 min-w-[14rem] bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-30 ${menuClassName}`}
          >
            {children}
          </div>
        </DropdownMenuContext.Provider>
      )}
    </div>
  );
};

export interface DropdownItemProps {
  onClick?: () => void;
  disabled?: boolean;
  /** Variant for destructive actions — styles the item in red. */
  variant?: 'default' | 'danger';
  icon?: React.ReactNode;
  children: React.ReactNode;
  /** If true, the menu stays open after clicking this item. */
  keepOpen?: boolean;
}

const DropdownItem: React.FC<DropdownItemProps> = ({
  onClick,
  disabled,
  variant = 'default',
  icon,
  children,
  keepOpen,
}) => {
  const ctx = React.useContext(DropdownMenuContext);
  const colorClass =
    variant === 'danger'
      ? 'text-red-600 hover:bg-red-50'
      : 'text-gray-700 hover:bg-gray-100';

  return (
    <button
      role="menuitem"
      onClick={() => {
        if (disabled) return;
        onClick?.();
        if (!keepOpen) ctx?.close();
      }}
      disabled={disabled}
      className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${colorClass}`}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span className="flex-1">{children}</span>
    </button>
  );
};

const DropdownSeparator: React.FC = () => <div className="my-1 border-t border-gray-100" />;

DropdownMenu.Item = DropdownItem;
DropdownMenu.Separator = DropdownSeparator;
